import axios, { AxiosError, AxiosPromise, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { VersionService } from './version.service';
import { IRequestHeaders } from '../interfaces/IRequsetHeaders';
import { HEADERS } from '../constants/constants';
import { IRequestOptions } from '../interfaces/IRequestOptions';
import { IUserService } from '../interfaces/IUserService';
import { IProjectService } from '../interfaces/IProjectService';
import { IUser } from '../interfaces/IUser';
import { IProject } from '../interfaces/IProject';
import { captureError } from '../utils/monitoring.util';

/**
 * Below this, a failed request is an expected outcome of a normal session rather than a defect:
 * an expired token (401), a permission boundary (403), a resource that is genuinely absent
 * (404). Callers and the 401 interceptor already handle those.
 */
const LOWEST_REPORTED_STATUS_CODE: number = 500;

/**
 * `true` once the document has begun going away. A request still in flight at that moment is
 * killed by the browser, and axios cannot tell that abort apart from a real transport failure:
 * both surface as `ERR_NETWORK` / "Network Error" with no response and therefore no status.
 *
 * `pagehide` rather than `beforeunload`/`unload`: it is the one teardown event that also fires
 * on the bfcache path and on mobile Safari, which is where a background tab is most likely to be
 * discarded mid-request.
 */
let isDocumentUnloading: boolean = false;

/**
 * Guarded because `shared/` is imported by the React Native app too, where a global `window`
 * exists but `addEventListener` does not.
 */
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
	window.addEventListener('pagehide', () => {
		isDocumentUnloading = true;
	});
}

/**
 * Whether a request that never received a response says anything about the application.
 *
 * A response-less failure arrives with `status === undefined`, which slipped straight past the
 * status floor above and was reported every time — 98 events on `Failed to POST
 * /auth/authentication/refresh, received status code undefined: Network Error` (FRONTEND-3H6),
 * plus the `StreamService` reconnect reports that cascade from the same refresh. The AI Assistant
 * page is the culprit precisely because it refreshes the token on its own before every turn
 * (`aiAssistant.api.ts`) ON TOP OF the 401 interceptor, the stream interceptors and the bootstrap
 * refresh, so it holds a refresh POST in flight far more often than any other page — and closing
 * the tab or following a link during one is an ordinary thing for a user to do, not a defect.
 *
 * What is deliberately still reported: a response-less failure while the page is alive and the
 * device is online. That is the shape a CORS regression or an unreachable gateway takes, and it
 * is worth waking up for.
 */
const getIsTransportFailureWorthReporting = (err: any): boolean => {
	if (axios.isCancel(err)) {
		return false;
	}
	if (isDocumentUnloading) {
		return false;
	}
	// `navigator.onLine === false` is a definite "there is no network interface". It is not
	// reliable in the other direction, so this suppresses only the case it is sure about.
	if (typeof navigator !== 'undefined' && navigator.onLine === false) {
		return false;
	}
	return true;
};

class RequestService {
	constructor(
		private baseUrl: string,
		private userService: IUserService,
		private projectService: IProjectService,
		private versionService: VersionService
	) {}

	public get = async (url: string, options?: IRequestOptions): Promise<any> => {
		try {
			const finalHeaders: IRequestHeaders = this.getRequestHeaders(options);
			const finalUrl: string = this.getApiUrl(url, options);
			const response: AxiosResponse<any> = await axios.get(finalUrl, {
				headers: finalHeaders,
				timeout: options?.timeout,
				params: options?.params,
			});
			return response.data;
		} catch (err: any) {
			err.message = `Failed to GET ${url}, received status code ${err.response?.status}: ${err.message}`;
			this.reportRequestFailure('GET', url, err);
			throw err;
		}
	};

	public post = async (url: string, options?: IRequestOptions): Promise<any> => {
		try {
			const finalHeaders: IRequestHeaders = this.getRequestHeaders(options);
			const finalUrl: string = this.getApiUrl(url, options);
			const response: AxiosResponse<any> = await axios.post(finalUrl, options?.body, {
				headers: finalHeaders,
				timeout: options?.timeout,
				params: options?.params,
			});
			return response.data;
		} catch (err: any) {
			err.message = `Failed to POST ${url}, received status code ${err.response?.status}: ${err.message}`;
			this.reportRequestFailure('POST', url, err);
			throw err;
		}
	};

	public put = async (url: string, options?: IRequestOptions): Promise<any> => {
		try {
			const finalHeaders: IRequestHeaders = this.getRequestHeaders(options);
			const finalUrl: string = this.getApiUrl(url, options);
			const response: AxiosResponse<any> = await axios.put(finalUrl, options?.body, {
				headers: finalHeaders,
				timeout: options?.timeout,
				params: options?.params,
			});
			return response.data;
		} catch (err: any) {
			err.message = `Failed to PUT ${url}, received status code ${err.response?.status}: ${err.message}`;
			this.reportRequestFailure('PUT', url, err);
			throw err;
		}
	};

	public delete = async (url: string, options?: IRequestOptions): Promise<any> => {
		try {
			const finalHeaders: IRequestHeaders = this.getRequestHeaders(options);
			const finalUrl: string = this.getApiUrl(url, options);
			const response: AxiosResponse<any> = await axios.delete(finalUrl, {
				headers: finalHeaders,
				timeout: options?.timeout,
				params: options?.params,
				data: options?.body,
			});
			return response.data;
		} catch (err: any) {
			err.message = `Failed to DELETE ${url}, received status code ${err.response?.status}: ${err.message}`;
			this.reportRequestFailure('DELETE', url, err);
			throw err;
		}
	};

	public resendRequest = (config: InternalAxiosRequestConfig): AxiosPromise<any> => axios(config);

	public addResponseErrorInterceptor = (callback: (error: AxiosError<any>) => void): void => {
		axios.interceptors.response.use(
			(response: AxiosResponse<any>) => response,
			(error: AxiosError<any>) => callback(error)
		);
	};

	public addRequestInterceptor = (
		callback: (req: InternalAxiosRequestConfig) => Promise<InternalAxiosRequestConfig>
	): void => {
		axios.interceptors.request.use(async (req) => callback(req));
	};

	private getRequestHeaders = (options?: IRequestOptions) => {
		const defaultHeaders: IRequestHeaders = options?.disableAuth ? {} : this.getDefaultHeaders();

		return {
			...defaultHeaders,
			...options?.headers,
		};
	};

	public getDefaultHeaders = (): IRequestHeaders => {
		try {
			const webClientVersion: string | null = this.versionService.getVersion();
			const userDetails: IUser | null = this.userService.getUserDetails();
			const userAccessToken: string | null = this.userService.getLoggedUserAccessToken();

			if (!userAccessToken) {
				return {
					[HEADERS.webClientVersion]: webClientVersion,
				};
			}

			const requestProject: IProject | undefined = this.projectService.getChosenProject();

			return {
				[HEADERS.authorization]: `Bearer ${userAccessToken}`,
				[HEADERS.tz]: requestProject?.tz,
				[HEADERS.projectId]: requestProject?.projectId,
				[HEADERS.organizationId]: requestProject?.organizationId,
				[HEADERS.username]: userDetails?.username,
				[HEADERS.webClientVersion]: webClientVersion,
				[HEADERS.isDemo]: !!requestProject?.isDemo,
			};
		} catch (err: any) {
			console.error('Failed to get request headers', err);
			throw err;
		}
	};

	private getApiUrl = (url: string, options?: IRequestOptions): string =>
		options?.isGlobal ? url : `${this.baseUrl}${url}`;

	/**
	 * Reports a failed request as an EXCEPTION with the url/method/status as structured metadata.
	 *
	 * It used to be `captureMessage(err.message)` on every failure, with the url and status
	 * interpolated into the message. Sentry groups messages by their text, so every distinct url
	 * — and the ids live in the paths — became its own issue: unbounded issue cardinality, no
	 * stack trace, and an error quota consumed almost entirely by 401/403/404s that are normal
	 * traffic. An exception groups by stack instead, and the detail rides as metadata where it
	 * can be searched without fragmenting the group.
	 */
	private reportRequestFailure = (method: string, url: string, err: any): void => {
		const status: number | undefined = err.response?.status;
		if (status !== undefined && status < LOWEST_REPORTED_STATUS_CODE) {
			return;
		}
		if (status === undefined && !getIsTransportFailureWorthReporting(err)) {
			return;
		}
		captureError(err, { method, url, status });
	};
}

export { RequestService };
